
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface AgeGroupOption {
  id: string;
  name: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export const useAgeGroupOptions = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: ageGroups = [], isLoading, error } = useQuery({
    queryKey: ['age-group-options'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('character_age_groups')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as AgeGroupOption[];
    },
  });

  const createAgeGroup = useMutation({
    mutationFn: async (name: string) => {
      const maxOrder = Math.max(...ageGroups.map(ag => ag.display_order), -1);
      const { data, error } = await supabase
        .from('character_age_groups')
        .insert([{ 
          name, 
          display_order: maxOrder + 1,
          is_active: true 
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['age-group-options'] });
      toast({
        title: "Success",
        description: "Age group created successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create age group",
        variant: "destructive",
      });
    },
  });

  const updateAgeGroup = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<AgeGroupOption> }) => {
      const { data, error } = await supabase
        .from('character_age_groups')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['age-group-options'] });
      toast({
        title: "Success",
        description: "Age group updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update age group",
        variant: "destructive",
      });
    },
  });

  const deleteAgeGroup = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('character_age_groups')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['age-group-options'] });
      toast({
        title: "Success",
        description: "Age group deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete age group",
        variant: "destructive",
      });
    },
  });

  const reorderAgeGroups = useMutation({
    mutationFn: async (reorderedItems: { id: string; display_order: number }[]) => {
      const updates = reorderedItems.map(item => 
        supabase
          .from('character_age_groups')
          .update({ display_order: item.display_order })
          .eq('id', item.id)
      );

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['age-group-options'] });
      toast({
        title: "Success",
        description: "Age group order updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to reorder age groups",
        variant: "destructive",
      });
    },
  });

  return {
    ageGroups,
    isLoading,
    error,
    createAgeGroup,
    updateAgeGroup,
    deleteAgeGroup,
    reorderAgeGroups,
  };
};
